import { isEmpty } from 'lodash';
import React, {
  useCallback,
  useState,
  useEffect,
  useReducer,
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import { useSelector } from 'react-redux';
import { getIsEditingForms } from 'src/features/hosLogs/store';
import { CHAR_CODE } from 'src/utils/charCodes';
import classnames from 'classnames';

import './hosFormsItem.scss';
interface Props {
  title: string;
  itemType: 'multiple' | 'single' | 'non-editable';
  value: string[];
  initialValue: string | string[];
  multipleValue?: string[];
  itemKey?: string;
  deleteArrayValue?: any;
  cleared?: boolean;
  onChange?: (value: string | string[]) => void;
}

export const HosFormsItem: React.FC<Props> = ({
  title,
  value,
  initialValue,
  itemType,
  cleared,
  multipleValue = [],
  itemKey,
  onChange,
  deleteArrayValue,
}) => {
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const initialState = typeof initialValue === 'string' ? value[0] : '';
  const [singleValue, setSingleValue] = useState(initialState);
  const isEditingForms = useSelector(getIsEditingForms);

  const onChangeInput = useCallback(
    event => {
      if (event.currentTarget.value === ' ') {
        setSingleValue('');
      } else {
        if (onChange) {
          onChange(event.currentTarget.value);
        }
        setSingleValue(event.currentTarget.value);
      }
    },
    [onChange],
  );

  const onKeyPressInput = useCallback(
    (event: KeyboardEvent) => {
      if (event.charCode === CHAR_CODE.SPACE) {
        if (singleValue) {
          multipleValue.push(singleValue);
          if (onChange) {
            onChange(multipleValue);
          }
          setSingleValue('');
        } else {
          event.preventDefault();
        }
      }
      if (event.charCode === CHAR_CODE.ENTER) {
        event.preventDefault();
      }
    },
    [singleValue, multipleValue, onChange],
  );

  const onKeyPressPreventDefault = useCallback((event: KeyboardEvent) => {
    if (event.charCode === CHAR_CODE.ENTER) {
      event.preventDefault();
    }
  }, []);

  const onEditArrayValue = useCallback(
    index => (event: ChangeEvent<HTMLInputElement>): void => {
      multipleValue.splice(index, 1, event.currentTarget.value);
      forceUpdate();
    },
    [multipleValue],
  );

  const clearSingleInput = useCallback(() => {
    setSingleValue('');
  }, []);

  useEffect(() => {
    if (cleared) {
      if (typeof initialValue === 'string') {
        setSingleValue(String(initialValue));
      }
    }
  }, [cleared]);

  const dataLabelStyle = classnames('hos-form-item__data-label-no-background', {
    'hos-form-item__data-label':
      (singleValue && singleValue.length === 0) || (singleValue && singleValue.length > 0),
  });

  const inputStyle = classnames('hos-form-item__input', {
    'hos-form-item__input hos-form-item__input--multiple': singleValue && singleValue.length === 0,
  });

  const multipleDataLabelStyle = classnames('hos-form-item__data-label', {
    'hos-form-item__data-label-no-background': isEditingForms === false,
  });

  const multipleInputStyle = classnames('hos-form-item__input hos-form-item__input--multiple', {
    'hos-form-item__input': isEditingForms === false,
  });

  const getWidthStyle = useCallback(value => {
    return {
      width: value ? `${(value.length + 1) * 8}px` : '120px',
    };
  }, []);

  const renderArrayType = (): JSX.Element => {
    return (
      <>
        {multipleValue.map((number, index) => (
          <span key={number} className={multipleDataLabelStyle}>
            {isEditingForms ? (
              <>
                <input
                  className={multipleInputStyle}
                  style={getWidthStyle(number)}
                  value={number}
                  onChange={onEditArrayValue(index)}
                  onKeyPress={onKeyPressPreventDefault}
                />
                <button
                  className="hos-form-item__delete-button"
                  onClick={e => {
                    e.preventDefault();
                    deleteArrayValue(number, itemKey);
                  }}
                >
                  х
                </button>
              </>
            ) : (
              <input
                className={multipleInputStyle}
                value={number}
                style={getWidthStyle(number)}
                readOnly
              />
            )}
          </span>
        ))}
        {isEditingForms && (
          <span className={dataLabelStyle}>
            <input
              className={inputStyle}
              value={singleValue}
              style={getWidthStyle(singleValue)}
              placeholder="Type to add more"
              onChange={onChangeInput}
              onKeyPress={onKeyPressInput}
            />
            {singleValue && singleValue.length > 0 && (
              <button className="hos-form-item__delete-button" onClick={clearSingleInput}>
                х
              </button>
            )}
          </span>
        )}
      </>
    );
  };

  const renderNoEditType = (): JSX.Element | null => {
    return !isEmpty(value) ? <span className="hos-form-item__input">{value[0]}</span> : null;
  };

  const renderInputType = (): JSX.Element => {
    return (
      <>
        {isEditingForms ? (
          <input
            className="hos-form-item__input"
            value={singleValue}
            placeholder="Type to add more"
            style={getWidthStyle(singleValue)}
            onChange={onChangeInput}
            onKeyPress={onKeyPressPreventDefault}
          />
        ) : (
          <input
            className="hos-form-item__input"
            value={singleValue}
            style={getWidthStyle(singleValue)}
            readOnly
          />
        )}
      </>
    );
  };

  return (
    <div className="forms-info">
      <div className="forms-info__box">
        <div className="forms-info__title">{title}</div>
        <div className="forms-info__data">
          {itemType === 'multiple' && renderArrayType()}
          {itemType === 'single' && renderInputType()}
          {itemType === 'non-editable' && renderNoEditType()}
        </div>
      </div>
    </div>
  );
};
